// Builds the canonical PostHog `$pageview` property bag from the browser, for
// the SDK-less beacon. posthog-js sets all of these client-side; we replicate
// the subset only the browser can know (URL, referrer, viewport, screen,
// timezone, language, campaign params, ids). The server (beacon route) layers on
// `$ip`, `$raw_user_agent` and parsed `$browser`/`$os`/`$device_type`.
//
// Keys use PostHog's `$`-prefixed names so Web Analytics and the standard
// breakdowns read them. The unprefixed `path` / `url` / `referrer` keys are kept
// because `site_events` and the daily rollup still consume those.
import { uuidv7 } from './uuid'

type PropertyValue = string | number | boolean | undefined

// Campaign/click identifiers PostHog recognises for channel attribution. We pass
// through whatever is present on the landing URL's query string.
const CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gad_source',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'ttclid',
  'li_fat_id',
  'igshid',
  'rdt_cid',
  'dclid',
] as const

const safeHost = (url: string): string => {
  try {
    return new URL(url).host
  } catch {
    return ''
  }
}

export const extractCampaignParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search)
  const out: Record<string, string> = {}
  for (const key of CAMPAIGN_PARAMS) {
    const value = params.get(key)
    if (value) {
      out[key] = value
    }
  }
  return out
}

export interface PageIdentity {
  sessionId: string
  windowId: string
  deviceId: string
  segment: string
}

export const buildClientPageviewProps = ({
  sessionId,
  windowId,
  deviceId,
  segment,
}: PageIdentity): Record<string, PropertyValue> => {
  const href = window.location.href
  const referrer = document.referrer
  const referringDomain = referrer ? safeHost(referrer) || '$direct' : '$direct'

  return {
    $session_id: sessionId,
    $window_id: windowId,
    $device_id: deviceId,
    $pageview_id: uuidv7(),
    $current_url: href,
    $host: window.location.host,
    $pathname: window.location.pathname,
    $referrer: referrer || '$direct',
    $referring_domain: referringDomain,
    title: document.title,
    $screen_height: window.screen?.height,
    $screen_width: window.screen?.width,
    $viewport_height: window.innerHeight,
    $viewport_width: window.innerWidth,
    $timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    $browser_language: navigator.language,
    // `$raw_user_agent` + parsed device props are added server-side in the
    // beacon route (it has the UA header) so we don't ship the long UA string
    // through every site_events row.
    ...extractCampaignParams(window.location.search),
    // Legacy keys still read by site_events (path column) and the rollup.
    path: window.location.pathname,
    url: href,
    referrer,
    segment,
  }
}
